import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faCheck, faTimes, faCog, faFileAlt, faTools, faShippingFast, faGift, faCreditCard, faBullhorn, faUsers, faStore } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import api from '../utils/api';
import AdminUsersPage from './AdminUsersPage';

interface Page {
  slug: string;
  title: string;
  updatedAt: string;
}

type TabId = 'general' | 'users' | 'pages' | 'maintenance' | 'tax' | 'payments' | 'announcements' | 'promoPopup';

interface TabMeta {
  id: TabId;
  label: string;
  icon: any;
  description: string;
}

const TAB_GROUPS: Array<{ title: string; tabs: TabMeta[] }> = [
  {
    title: 'Store',
    tabs: [
      { id: 'general', label: 'General', icon: faStore, description: 'Business identity, contact info, and shipping address.' },
      { id: 'tax', label: 'Tax & Shipping', icon: faShippingFast, description: 'Sales tax, shipping rates, and every fee or markup applied across the store.' },
      { id: 'payments', label: 'Payments', icon: faCreditCard, description: 'Enable payment methods and manage API credentials.' },
    ],
  },
  {
    title: 'Content',
    tabs: [
      { id: 'pages', label: 'Content Pages', icon: faFileAlt, description: 'Warranty, terms, privacy, and other policy pages.' },
      { id: 'announcements', label: 'Announcement Bar', icon: faBullhorn, description: 'Rotating messages at the top of the storefront.' },
      { id: 'promoPopup', label: 'Promo Popup', icon: faGift, description: 'Timed marketing modal shown to visitors.' },
    ],
  },
  {
    title: 'Access',
    tabs: [
      { id: 'users', label: 'Team', icon: faUsers, description: 'Admin and staff accounts.' },
      { id: 'maintenance', label: 'Availability', icon: faTools, description: 'Maintenance mode and per-page toggles.' },
    ],
  },
];

const ALL_TABS: TabMeta[] = TAB_GROUPS.flatMap(g => g.tabs);

const AdminSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('general');

  // --- Settings State ---
  const [settings, setSettings] = useState<Record<string, any>>({
    storeName: '',
    email: '',
    phone: '',
    address: { street: '', city: '', state: '', zipCode: '', country: 'US' },
    taxRate: 8.0,
    taxEnabled: true,
    currency: 'USD',
    flatShippingRate: 29.99,
    freeShippingThreshold: 500,
    buildFeePercentage: 10,
    partsScraperMarkupPercentage: 10,
    productScraperMarkupPercentage: 20,
    productMarkupPercentage: 20,
    shippingMarkupPercentage: 20,
    manualOrderIntegrationFeePercentage: 20,
    adminProductMarkupPercentage: 20,
    shippingInsurancePercentage: 1.25,
    socialLinks: { facebook: '', twitter: '', instagram: '', youtube: '' },
    paymentMethods: {
      checkout: { card: true, paypal: true, affirm: true },
      invoices: { paypal: true },
    },
    paymentCredentials: {
      paypal: { testMode: true, clientId: '', clientSecret: '' },
      square: { testMode: true, appId: '', accessToken: '', locationId: '' },
      affirm: { testMode: true, publicKey: '', privateKey: '' },
    },
  });

  const [pageStatuses, setPageStatuses] = useState<any[]>([]);
  const [maintenance, setMaintenance] = useState<any>({ enabled: false });
  const [announcementBar, setAnnouncementBar] = useState<any>({ announcements: [], rotationSpeed: 5, isVisible: true });
  const [promoPopup, setPromoPopup] = useState<any>({ isEnabled: false, delaySeconds: 60, htmlContent: '', frequencyHours: 24 });
  const [promoPreview, setPromoPreview] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // --- Pages Content State ---
  const [pagesList, setPagesList] = useState<Page[]>([]);
  const [selectedPageSlug, setSelectedPageSlug] = useState<string | null>(null);
  const [pageFormData, setPageFormData] = useState({ title: '', content: '' });
  const [isPagesLoading, setIsPagesLoading] = useState(false);
  const [isPageSaving, setIsPageSaving] = useState(false);
  const [pageMessage, setPageMessage] = useState({ type: '', text: '' });
  const [isPreview, setIsPreview] = useState(false);

  const predefinedPages = [
    { slug: 'warranty', label: 'Warranty Policy' },
    { slug: 'tos', label: 'Terms of Service' },
    { slug: 'privacy-policy', label: 'Privacy Policy' },
    { slug: 'cookie-policy', label: 'Cookie Policy' },
    { slug: 'shipping-returns', label: 'Shipping & Returns' }
  ];

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const [businessRes, pageRes, announcementRes, promoPopupRes] = await Promise.all([
        api.get('/business'),
        api.get('/page-status'),
        api.get('/announcement-bar'),
        api.get('/promo-popup')
      ]);

      if (businessRes.data.businessInfo) {
        const bizInfo = businessRes.data.businessInfo;
        const pc = bizInfo.paymentCredentials || {};
        // Backend now masks secrets: instead of the actual value we get a
        // `has<Field>` boolean. Secret fields start blank; if the admin
        // leaves them blank on save, the backend preserves the stored
        // value. Typing a new value overwrites.
        setSettings(prev => ({
          ...prev,
          ...bizInfo,
          taxEnabled: bizInfo.taxEnabled === true,
          paymentMethods: {
            checkout: {
              card: bizInfo.paymentMethods?.checkout?.card !== false,
              paypal: bizInfo.paymentMethods?.checkout?.paypal !== false,
              affirm: bizInfo.paymentMethods?.checkout?.affirm !== false,
            },
            invoices: {
              paypal: bizInfo.paymentMethods?.invoices?.paypal !== false,
            },
          },
          paymentCredentials: {
            paypal: {
              testMode: pc.paypal?.testMode !== false,
              clientId: pc.paypal?.clientId || '',
              clientSecret: '',
              hasClientSecret: !!pc.paypal?.hasClientSecret,
            },
            square: {
              testMode: pc.square?.testMode !== false,
              appId: pc.square?.appId || '',
              accessToken: '',
              hasAccessToken: !!pc.square?.hasAccessToken,
              locationId: pc.square?.locationId || '',
            },
            affirm: {
              testMode: pc.affirm?.testMode !== false,
              publicKey: pc.affirm?.publicKey || '',
              privateKey: '',
              hasPrivateKey: !!pc.affirm?.hasPrivateKey,
            },
          },
        }));
      }

      if (pageRes.data.pages) {
        const pages = pageRes.data.pages;
        const maint = pages.find((p: any) => p.path === 'maintenance_mode');
        if (maint) {
          setMaintenance(maint);
        }
        setPageStatuses(pages.filter((p: any) => p.path !== 'maintenance_mode'));
      }

      if (announcementRes.data.announcementBar) {
        setAnnouncementBar(announcementRes.data.announcementBar);
      }

      if (promoPopupRes.data.promoPopup) {
        setPromoPopup(promoPopupRes.data.promoPopup);
      }
    } catch (error) {
      console.error('Failed to fetch info', error);
      setMessage({ type: 'error', text: 'Failed to load information.' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      setIsPagesLoading(true);
      const response = await api.get('/pages');
      if (response.data.pages) {
        setPagesList(response.data.pages);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setIsPagesLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchPages();
  }, []);

  const handleChange = (key: string, value: any) => {
    if (!key.includes('.')) {
      setSettings(prev => ({ ...prev, [key]: value }));
      return;
    }
    // Walks arbitrary dot-paths like "paymentMethods.checkout.card" so the
    // Payments panel can toggle nested flags without collapsing sibling keys.
    const path = key.split('.');
    setSettings(prev => {
      const next: any = { ...prev };
      let cursor: any = next;
      for (let i = 0; i < path.length - 1; i++) {
        cursor[path[i]] = { ...(cursor[path[i]] || {}) };
        cursor = cursor[path[i]];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const payload: Record<string, any> = {
        ...settings,
        taxEnabled: settings.taxEnabled === true
      };
      
      await api.put('/business', payload);
      
      // Save maintenance mode
      await api.put('/page-status/maintenance_mode', {
        enabled: maintenance.enabled,
        reopenAt: maintenance.reopenAt
      });

      // Save page statuses
      for (const p of pageStatuses) {
        await api.put(`/page-status/${encodeURIComponent(p.path)}`, {
          enabled: p.enabled
        });
      }

      // Save announcement bar
      await api.put('/announcement-bar', announcementBar);

      // Save promo popup
      await api.put('/promo-popup', {
        isEnabled: !!promoPopup.isEnabled,
        delaySeconds: Number(promoPopup.delaySeconds) || 0,
        htmlContent: promoPopup.htmlContent || '',
        frequencyHours: Number(promoPopup.frequencyHours) || 0,
      });

      await fetchSettings();
      
      setMessage({ type: 'success', text: 'Settings saved successfully.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save settings', error);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const recalculatePrices = async () => {
    if (!window.confirm('Recalculate all part and product retail prices from current costs and markup percentages? Save your settings first if you just changed them.')) return;
    setIsRecalculating(true);
    setMessage(null);
    try {
      const res = await api.post('/products/recalculate');
      setMessage({ type: 'success', text: res.data?.message || 'Prices recalculated.' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err: any) {
      console.error('Recalculate failed', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to recalculate prices.' });
    } finally {
      setIsRecalculating(false);
    }
  };

  const loadPageContent = async (slug: string) => {
    try {
      const response = await api.get(`/pages/${slug}`);
      if (response.data.page) {
        setPageFormData({ title: response.data.page.title, content: response.data.page.content || '' });
        setSelectedPageSlug(slug);
        setIsPreview(false);
        setPageMessage({ type: '', text: '' });
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const handleCreateNewOrSelectPage = (slug: string, label: string) => {
    const existing = pagesList.find(p => p.slug === slug);
    if (existing) {
      loadPageContent(slug);
    } else {
      setPageFormData({ title: label, content: '' });
      setSelectedPageSlug(slug);
      setIsPreview(false);
      setPageMessage({ type: '', text: '' });
    }
  };

  const handleSavePage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPageSlug) return;

    try {
      setIsPageSaving(true);
      setPageMessage({ type: '', text: '' });
      const response = await api.put(`/pages/${selectedPageSlug}`, pageFormData);

      if (response.data.page) {
        setPageMessage({ type: 'success', text: 'Page updated successfully!' });
        fetchPages(); // Refresh the list to get updated dates
      }
    } catch (error: any) {
      console.error('Error saving page:', error);
      setPageMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update page' });
    } finally {
      setIsPageSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <span className="text-sm">Loading settings...</span>
        </div>
      </div>
    );
  }

  const currentTab = ALL_TABS.find(t => t.id === activeTab) || ALL_TABS[0];
  const showSaveBar = activeTab !== 'pages' && activeTab !== 'users';

  const sidebar = (
    <aside className="lg:w-56 shrink-0">
      <div className="lg:sticky lg:top-0 bg-[#0a0c13]/60 border border-[#1f2233] rounded-xl p-3 lg:p-4">
        <div className="mb-4 px-2">
          <h1 className="text-sm font-semibold text-white">Settings</h1>
        </div>
        <div className="space-y-4">
          {TAB_GROUPS.map(group => (
            <div key={group.title}>
              <div className="px-2 mb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {group.title}
              </div>
              <div className="space-y-0.5">
                {group.tabs.map(tab => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-md text-sm text-left transition-colors ${
                        isActive
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'text-slate-400 hover:text-white hover:bg-[#11141d]'
                      }`}
                    >
                      <FontAwesomeIcon icon={tab.icon} className="w-3.5 h-3.5 shrink-0 opacity-80" />
                      <span className="truncate">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {sidebar}

      {/* Content pane */}
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        {/* Page header */}
        <div className="pb-3 border-b border-[#1f2233]/60">
          <h2 className="text-xl font-semibold text-white">{currentTab.label}</h2>
          <p className="text-sm text-slate-500 mt-1">{currentTab.description}</p>
        </div>

        {/* Status banner */}
        {message && activeTab !== 'pages' && (
          <div className={`p-3 rounded-lg border text-sm ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400'
              : 'bg-red-500/10 border-red-500/40 text-red-400'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tab body */}
        <div className={showSaveBar ? 'pb-20' : ''}>
        {/* USERS TAB */}
        {activeTab === 'users' && (
          <AdminUsersPage />
        )}

        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <div className="admin-card overflow-hidden max-w-4xl">
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Store Name</label>
                  <input
                    type="text"
                    className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                    value={settings.storeName || ''}
                    onChange={e => handleChange('storeName', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Support Email</label>
                  <input
                    type="email"
                    className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                    value={settings.email || ''}
                    onChange={e => handleChange('email', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                    value={settings.phone || ''}
                    onChange={e => handleChange('phone', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Currency</label>
                  <select
                    className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                    value={settings.currency || 'USD'}
                    onChange={e => handleChange('currency', e.target.value)}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD (£)</option>
                  </select>
                </div>
                
                {/* Address fields */}
                <div className="md:col-span-2 pt-4 border-t border-[#1f2233]">
                  <h3 className="text-md font-medium text-white mb-4">Physical Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-400 mb-1">Street Address</label>
                      <input type="text" className="admin-input w-full bg-[#0a0c13] border-[#1f2233] rounded-lg" value={settings.address?.street || ''} onChange={e => handleChange('address.street', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">City</label>
                      <input type="text" className="admin-input w-full bg-[#0a0c13] border-[#1f2233] rounded-lg" value={settings.address?.city || ''} onChange={e => handleChange('address.city', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">State/Province</label>
                      <input type="text" className="admin-input w-full bg-[#0a0c13] border-[#1f2233] rounded-lg" value={settings.address?.state || ''} onChange={e => handleChange('address.state', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Zip/Postal Code</label>
                      <input type="text" className="admin-input w-full bg-[#0a0c13] border-[#1f2233] rounded-lg" value={settings.address?.zipCode || ''} onChange={e => handleChange('address.zipCode', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Country</label>
                      <input type="text" className="admin-input w-full bg-[#0a0c13] border-[#1f2233] rounded-lg" value={settings.address?.country || 'US'} onChange={e => handleChange('address.country', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PAGES TAB */}
        {activeTab === 'pages' && (
          <div className="flex flex-col md:flex-row gap-6 h-full max-w-6xl">
            {/* Page-picker sidebar */}
            <div className="w-full md:w-56 shrink-0">
              <div className="bg-[#11141d] rounded-lg p-4 border border-[#1f2233] h-full">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Policy Pages</div>
                
                {isPagesLoading ? (
                  <div className="flex justify-center p-4">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-emerald-500 text-xl" />
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {predefinedPages.map(page => {
                      const exists = pagesList.find(p => p.slug === page.slug);
                      return (
                        <li key={page.slug}>
                          <button
                            onClick={() => handleCreateNewOrSelectPage(page.slug, page.label)}
                            className={`w-full text-left px-3 py-2 rounded-md transition-colors flex justify-between items-center ${
                              selectedPageSlug === page.slug
                                ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'
                                : 'hover:bg-[#1f2233] text-slate-300'
                            }`}
                          >
                            <span>{page.label}</span>
                            {exists ? (
                              <FontAwesomeIcon icon={faCheck} className="text-emerald-500 text-xs" />
                            ) : (
                              <FontAwesomeIcon icon={faTimes} className="text-slate-500 text-xs" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                
                <div className="mt-4 pt-4 border-t border-[#1f2233]">
                  <p className="text-xs text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faCheck} className="text-emerald-500 mr-1" /> = Has content
                  </p>
                  <p className="text-xs text-slate-400">
                    <FontAwesomeIcon icon={faTimes} className="text-slate-500 mr-1" /> = Empty/Default
                  </p>
                </div>
              </div>
            </div>

            {/* Editor Area */}
            <div className="flex-grow">
              {selectedPageSlug ? (
                <div className="bg-[#11141d] rounded-lg p-6 border border-[#1f2233] h-full flex flex-col">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-medium text-white">Edit Content</h2>
                    <div className="flex bg-[#0a0c13] rounded-md p-1 border border-[#1f2233]">
                      <button
                        onClick={() => setIsPreview(false)}
                        className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                          !isPreview ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        Write
                      </button>
                      <button
                        onClick={() => setIsPreview(true)}
                        className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                          isPreview ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        Preview
                      </button>
                    </div>
                  </div>

                  {pageMessage.text && (
                    <div className={`p-4 mb-6 rounded-md ${
                      pageMessage.type === 'success' ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800' : 'bg-red-900/30 text-red-400 border border-red-800'
                    }`}>
                      {pageMessage.text}
                    </div>
                  )}

                  <form onSubmit={handleSavePage} className="flex flex-col flex-grow">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-slate-400 mb-1">Page Title</label>
                      <input
                        type="text"
                        value={pageFormData.title}
                        onChange={(e) => setPageFormData({ ...pageFormData, title: e.target.value })}
                        className="w-full bg-[#0a0c13] border border-[#1f2233] rounded-md px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        required
                      />
                    </div>

                    <div className="mb-6 flex-grow flex flex-col">
                      <label className="block text-sm font-medium text-slate-400 mb-1">
                        Content <span className="text-xs text-slate-500 font-normal">(Markdown supported)</span>
                      </label>
                      
                      {isPreview ? (
                        <div className="w-full flex-grow min-h-[400px] bg-[#0a0c13] border border-[#1f2233] rounded-md p-6 overflow-y-auto prose prose-invert max-w-4xl mx-auto text-left text-slate-300 prose-headings:text-white prose-h1:text-4xl prose-h2:text-2xl prose-h3:text-xl prose-p:leading-relaxed prose-a:text-emerald-400 hover:prose-a:text-emerald-300 prose-strong:text-white prose-ul:list-disc prose-ol:list-decimal prose-li:my-1">
                          {pageFormData.content ? (
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm, remarkBreaks]} 
                              rehypePlugins={[rehypeRaw]}
                            >
                              {pageFormData.content}
                            </ReactMarkdown>
                          ) : (
                            <p className="text-slate-500 italic">Nothing to preview</p>
                          )}
                        </div>
                      ) : (
                        <textarea
                          value={pageFormData.content}
                          onChange={(e) => setPageFormData({ ...pageFormData, content: e.target.value })}
                          className="w-full flex-grow min-h-[400px] bg-[#0a0c13] border border-[#1f2233] rounded-md px-4 py-4 text-white focus:outline-none focus:border-emerald-500 font-mono text-sm"
                          placeholder="# Heading&#10;&#10;Write your content here using Markdown..."
                          required
                        />
                      )}
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        disabled={isPageSaving}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors font-medium flex items-center space-x-2 disabled:opacity-50"
                      >
                        {isPageSaving ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faSave} />
                        )}
                        <span>{isPageSaving ? 'Saving...' : 'Save Page Content'}</span>
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="bg-[#11141d] rounded-lg p-10 border border-[#1f2233] text-center flex flex-col items-center justify-center h-full min-h-[400px]">
                  <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mb-4 text-slate-400">
                    <FontAwesomeIcon icon={faFileAlt} size="xl" />
                  </div>
                  <h2 className="text-xl font-medium mb-2 text-white">Select a Page to Edit</h2>
                  <p className="text-slate-400 max-w-md">
                    Choose a page from the sidebar to edit its content. You can write content using Markdown format and preview it before saving.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MAINTENANCE TAB */}
        {activeTab === 'maintenance' && (
          <div className="admin-card overflow-hidden max-w-5xl">
            <div className="p-6 space-y-6">
              <div className="bg-[#0a0c13] p-6 rounded-xl border border-[#1f2233]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-white">Maintenance Mode</h3>
                    <p className="text-sm text-slate-400">When enabled, the site will be inaccessible to non-admin users.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={maintenance?.enabled || false}
                      onChange={(e) => {
                        setMaintenance((prev: any) => ({ ...prev, enabled: e.target.checked }));
                      }}
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                  </label>
                </div>
                
                {maintenance?.enabled && (
                  <div className="mt-4 pt-4 border-t border-[#1f2233]">
                    <label className="block text-sm font-medium text-slate-400 mb-2">Auto-Reopen Time (EST)</label>
                    <input
                      type="datetime-local"
                      className="admin-input bg-[#0a0c13] border-[#1f2233] focus:border-red-500 focus:ring-2 focus:ring-red-500/20 rounded-xl text-white"
                      value={maintenance?.reopenAt ? (() => {
                        try {
                          const estString = new Date(maintenance.reopenAt).toLocaleString("sv-SE", { timeZone: "America/New_York" });
                          return estString.replace(' ', 'T').slice(0, 16);
                        } catch (e) {
                          return '';
                        }
                      })() : ''}
                      onChange={(e) => {
                        const estDateTimeLocalStr = e.target.value;
                        if (!estDateTimeLocalStr) {
                          setMaintenance((prev: any) => ({ ...prev, reopenAt: undefined }));
                          return;
                        }
                        
                        const d4 = new Date(estDateTimeLocalStr + "-04:00");
                        const d5 = new Date(estDateTimeLocalStr + "-05:00");
                        const fmt = (d: Date) => d.toLocaleString("sv-SE", { timeZone: "America/New_York" }).replace(' ', 'T').slice(0, 16);
                        
                        let finalIso = d5.toISOString();
                        if (fmt(d4) === estDateTimeLocalStr) {
                          finalIso = d4.toISOString();
                        } else if (fmt(d5) === estDateTimeLocalStr) {
                          finalIso = d5.toISOString();
                        }

                        setMaintenance((prev: any) => ({ ...prev, reopenAt: finalIso }));
                      }}
                    />
                    <p className="text-xs text-slate-500 mt-2">Enter the exact time in EST (Eastern Standard/Daylight Time). Leave blank to require manual reopening.</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium text-white mb-4">Page Toggles</h3>
                <p className="text-sm text-slate-400 mb-4">Toggle switches to disable specific pages. Disabled pages will show a maintenance or 404 message.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pageStatuses.map((page: any) => {
                    return (
                      <div key={page.path} className="flex items-center justify-between p-3 bg-[#0a0c13] rounded-lg border border-[#1f2233]">
                        <div>
                          <span className="text-sm font-medium text-white">{page.name}</span>
                          <span className="block text-xs text-slate-500">{page.path}</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={page.enabled}
                            onChange={(e) => {
                              const isEnabled = e.target.checked;
                              setPageStatuses(prev => 
                                prev.map(p => p.path === page.path ? { ...p, enabled: isEnabled } : p)
                              );
                            }}
                          />
                          <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ANNOUNCEMENTS TAB */}
        {activeTab === 'announcements' && (
          <div className="admin-card overflow-hidden max-w-4xl">
            <div className="p-6 border-b border-[#1f2233] flex justify-between items-center">
              <span className="text-sm text-slate-400">Show the bar on the storefront</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={announcementBar?.isVisible ?? true}
                  onChange={(e) => {
                    setAnnouncementBar((prev: any) => ({ ...prev, isVisible: e.target.checked }));
                  }}
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                <span className="ml-3 text-sm font-medium text-slate-300">Visible</span>
              </label>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Rotation Speed (seconds)</label>
                <input
                  type="number"
                  min="1"
                  className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                  value={announcementBar?.rotationSpeed || 5}
                  onChange={(e) => setAnnouncementBar((prev: any) => ({ ...prev, rotationSpeed: parseInt(e.target.value) || 5 }))}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-md font-medium text-white">Announcements</h3>
                  <button
                    onClick={() => {
                      setAnnouncementBar((prev: any) => ({
                        ...prev,
                        announcements: [...(prev.announcements || []), { text: '', link: '', isActive: true }]
                      }));
                    }}
                    className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg text-sm transition-colors"
                  >
                    + Add Announcement
                  </button>
                </div>

                <div className="space-y-4">
                  {(!announcementBar.announcements || announcementBar.announcements.length === 0) ? (
                    <div className="text-center p-6 bg-[#0a0c13] rounded-xl border border-[#1f2233] text-slate-500">
                      No announcements added yet.
                    </div>
                  ) : (
                    announcementBar.announcements.map((ann: any, index: number) => (
                      <div key={index} className="p-4 bg-[#0a0c13] rounded-xl border border-[#1f2233] space-y-4 relative group">
                        <button
                          onClick={() => {
                            setAnnouncementBar((prev: any) => ({
                              ...prev,
                              announcements: prev.announcements.filter((_: any, i: number) => i !== index)
                            }));
                          }}
                          className="absolute top-4 right-4 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>

                        <div>
                          <label className="block text-xs font-medium text-slate-400 mb-1">Text</label>
                          <input
                            type="text"
                            className="admin-input w-full bg-[#11141d] border-[#1f2233] focus:border-emerald-500 rounded-lg"
                            value={ann.text}
                            onChange={(e) => {
                              const newAnn = [...announcementBar.announcements];
                              newAnn[index].text = e.target.value;
                              setAnnouncementBar({ ...announcementBar, announcements: newAnn });
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1">Link (optional)</label>
                            <input
                              type="text"
                              className="admin-input w-full bg-[#11141d] border-[#1f2233] focus:border-emerald-500 rounded-lg"
                              value={ann.link || ''}
                              onChange={(e) => {
                                const newAnn = [...announcementBar.announcements];
                                newAnn[index].link = e.target.value;
                                setAnnouncementBar({ ...announcementBar, announcements: newAnn });
                              }}
                            />
                          </div>
                          <div className="flex items-end pb-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ann.isActive}
                                onChange={(e) => {
                                  const newAnn = [...announcementBar.announcements];
                                  newAnn[index].isActive = e.target.checked;
                                  setAnnouncementBar({ ...announcementBar, announcements: newAnn });
                                }}
                                className="rounded bg-[#11141d] border-[#1f2233] text-emerald-500 focus:ring-emerald-500/20"
                              />
                              <span className="text-sm text-slate-300">Active</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PROMO POPUP TAB */}
        {activeTab === 'promoPopup' && (
          <div className="admin-card overflow-hidden max-w-4xl">
            <div className="p-6 border-b border-[#1f2233] flex justify-between items-center">
              <span className="text-sm text-slate-400">Show the popup to visitors</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!!promoPopup?.isEnabled}
                  onChange={(e) => setPromoPopup((prev: any) => ({ ...prev, isEnabled: e.target.checked }))}
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                <span className="ml-3 text-sm font-medium text-slate-300">Enabled</span>
              </label>
            </div>
            <div className="p-6 space-y-6">
              <p className="text-sm text-slate-400">
                Shows a promotional popup to visitors after they've been browsing for a configurable amount of time. Use it to surface a discount code, sale, or promotion to keep visitors engaged. Content accepts raw HTML.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Delay Before Showing (seconds)</label>
                  <input
                    type="number"
                    min={0}
                    className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                    value={promoPopup?.delaySeconds ?? 60}
                    onChange={(e) => setPromoPopup((prev: any) => ({ ...prev, delaySeconds: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-slate-500 mt-2">Time the visitor spends on the site before the popup appears. ~60 is a good starting point.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Show Again After (hours)</label>
                  <input
                    type="number"
                    min={0}
                    className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl"
                    value={promoPopup?.frequencyHours ?? 24}
                    onChange={(e) => setPromoPopup((prev: any) => ({ ...prev, frequencyHours: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-slate-500 mt-2">Once a visitor dismisses the popup, don't show it again for this many hours. 0 = always show.</p>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-slate-400">
                    HTML Content
                  </label>
                  <div className="flex bg-[#0a0c13] rounded-md p-1 border border-[#1f2233]">
                    <button
                      type="button"
                      onClick={() => setPromoPreview(false)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${!promoPreview ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      Write
                    </button>
                    <button
                      type="button"
                      onClick={() => setPromoPreview(true)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${promoPreview ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      Preview
                    </button>
                  </div>
                </div>

                {promoPreview ? (
                  <div className="w-full min-h-[260px] bg-[#0a0c13] border border-[#1f2233] rounded-md p-6 overflow-y-auto">
                    {promoPopup?.htmlContent ? (
                      <div dangerouslySetInnerHTML={{ __html: promoPopup.htmlContent }} />
                    ) : (
                      <p className="text-slate-500 italic">Nothing to preview</p>
                    )}
                  </div>
                ) : (
                  <textarea
                    value={promoPopup?.htmlContent || ''}
                    onChange={(e) => setPromoPopup((prev: any) => ({ ...prev, htmlContent: e.target.value }))}
                    className="w-full min-h-[260px] bg-[#0a0c13] border border-[#1f2233] rounded-md px-4 py-3 text-white focus:outline-none focus:border-emerald-500 font-mono text-sm"
                    placeholder={'<div style="text-align:center">\n  <h2 style="font-size:1.75rem;font-weight:800;color:#10b981">Save 10% Today</h2>\n  <p style="margin-top:.5rem;color:#cbd5e1">Use code <strong>WELCOME10</strong> at checkout.</p>\n</div>'}
                  />
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Tip: keep the content compact — visitors see this as a modal overlay. A close button is always rendered automatically.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TAX & SHIPPING TAB */}
        {activeTab === 'tax' && (
          <div className="admin-card overflow-hidden max-w-4xl">
            <div className="p-6 space-y-8">
              {/* Shipping & tax */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Shipping & Tax</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Flat Shipping Rate</label>
                    <div className="relative">
                      <span className="absolute left-4 top-2.5 text-slate-500">$</span>
                      <input
                        type="number"
                        className="admin-input w-full pl-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.flatShippingRate || 0}
                        onChange={e => handleChange('flatShippingRate', parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Free Shipping Threshold</label>
                    <div className="relative">
                      <span className="absolute left-4 top-2.5 text-slate-500">$</span>
                      <input
                        type="number"
                        className="admin-input w-full pl-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.freeShippingThreshold || 0}
                        onChange={e => handleChange('freeShippingThreshold', parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Base Tax Rate (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.taxRate || 0}
                        onChange={e => handleChange('taxRate', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Shipping Rate Markup (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.shippingMarkupPercentage ?? 20}
                        onChange={e => handleChange('shippingMarkupPercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Applied to live carrier rates before showing to customers.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Shipping Insurance (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.shippingInsurancePercentage ?? 1.25}
                        onChange={e => handleChange('shippingInsurancePercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Optional insurance calculated on subtotal + shipping.</p>
                  </div>
                  <div className="flex items-center md:col-span-2">
                    <label className="flex items-center space-x-3 cursor-pointer text-slate-300 hover:text-white transition-colors">
                      <input
                        type="checkbox"
                        className="w-5 h-5 rounded bg-[#0a0c13] border-[#1f2233] text-emerald-500 focus:ring-emerald-500/20"
                        checked={settings.taxEnabled || false}
                        onChange={e => handleChange('taxEnabled', e.target.checked)}
                      />
                      <span>Enable automated tax calculation</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Fees & markups */}
              <div className="border-t border-[#1f2233] pt-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Fees & Markups</h3>
                    <p className="text-xs text-slate-500 mt-1">Every upcharge applied anywhere in the store. Changes take effect immediately for new orders. Existing parts and products keep their current prices until you recalculate.</p>
                  </div>
                  <button
                    type="button"
                    onClick={recalculatePrices}
                    disabled={isRecalculating}
                    className="shrink-0 text-sm px-4 py-2 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors border border-emerald-500/30 font-medium disabled:opacity-50"
                  >
                    {isRecalculating ? 'Recalculating…' : 'Recalculate Now'}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">System Integration & Validation (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.buildFeePercentage ?? 10}
                        onChange={e => handleChange('buildFeePercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Build fee shown to customers in the configurator.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Manual Order Integration Fee (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.manualOrderIntegrationFeePercentage ?? 20}
                        onChange={e => handleChange('manualOrderIntegrationFeePercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Applied when staff assembles a custom build via the admin "Add Order" page.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Parts Scraper Markup (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.partsScraperMarkupPercentage ?? 10}
                        onChange={e => handleChange('partsScraperMarkupPercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Retail markup applied when the scraper imports/updates individual PC parts.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Product Scraper Markup (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.productScraperMarkupPercentage ?? 20}
                        onChange={e => handleChange('productScraperMarkupPercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Retail markup applied to pre-built products when a component's price changes.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Product Recalculation Markup (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.productMarkupPercentage ?? 20}
                        onChange={e => handleChange('productMarkupPercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Used by the manual "Recalculate Now" action for product retail pricing.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Admin Product Markup Button (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        className="admin-input w-full pr-8 bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white"
                        value={settings.adminProductMarkupPercentage ?? 20}
                        onChange={e => handleChange('adminProductMarkupPercentage', parseFloat(e.target.value))}
                      />
                      <span className="absolute right-4 top-2.5 text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Suggested markup shown on the "Add/Edit Product" page's quick-apply button.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <div className="admin-card overflow-hidden max-w-5xl">
            <div className="p-6 space-y-8">
              {/* Checkout */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Checkout</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {([
                    { key: 'card', label: 'Credit / Debit Cards', desc: 'Visa, Mastercard, Amex via Square' },
                    { key: 'paypal', label: 'PayPal', desc: 'Pay in full' },
                    { key: 'affirm', label: 'Affirm', desc: 'Pay over time (US only)' },
                  ] as const).map(m => {
                    const enabled = !!settings.paymentMethods?.checkout?.[m.key];
                    return (
                      <label
                        key={m.key}
                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          enabled
                            ? 'border-emerald-500/40 bg-emerald-500/5'
                            : 'border-[#1f2233] bg-[#0a0c13] hover:border-[#2a2f44]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 w-4 h-4 rounded bg-[#0a0c13] border-[#1f2233] text-emerald-500 focus:ring-emerald-500/20"
                          checked={enabled}
                          onChange={e => handleChange(`paymentMethods.checkout.${m.key}`, e.target.checked)}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white">{m.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{m.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Invoices */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Invoices</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {([
                    { key: 'paypal', label: 'PayPal', desc: 'Only method available on invoice pages today' },
                  ] as const).map(m => {
                    const enabled = !!settings.paymentMethods?.invoices?.[m.key];
                    return (
                      <label
                        key={m.key}
                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          enabled
                            ? 'border-emerald-500/40 bg-emerald-500/5'
                            : 'border-[#1f2233] bg-[#0a0c13] hover:border-[#2a2f44]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 w-4 h-4 rounded bg-[#0a0c13] border-[#1f2233] text-emerald-500 focus:ring-emerald-500/20"
                          checked={enabled}
                          onChange={e => handleChange(`paymentMethods.invoices.${m.key}`, e.target.checked)}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white">{m.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{m.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* API Credentials */}
              <div className="border-t border-[#1f2233] pt-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2">API Credentials</h3>
                <p className="text-xs text-slate-500 mb-6">
                  Test Mode routes traffic to the processor's sandbox endpoints — safe to charge with test cards. Turn off for live money.
                </p>

                {([
                  {
                    id: 'paypal', label: 'PayPal',
                    fields: [
                      { key: 'clientId', label: 'Client ID', secret: false, placeholder: 'AXX...' },
                      { key: 'clientSecret', label: 'Client Secret', secret: true, placeholder: '••••' },
                    ],
                  },
                  {
                    id: 'square', label: 'Square',
                    fields: [
                      { key: 'appId', label: 'Application ID', secret: false, placeholder: 'sandbox-sq0idb-...' },
                      { key: 'accessToken', label: 'Access Token', secret: true, placeholder: 'EAAA...' },
                      { key: 'locationId', label: 'Location ID', secret: false, placeholder: 'Optional — auto-detected' },
                    ],
                  },
                  {
                    id: 'affirm', label: 'Affirm',
                    fields: [
                      { key: 'publicKey', label: 'Public API Key', secret: false, placeholder: 'DH2X...' },
                      { key: 'privateKey', label: 'Private API Key', secret: true, placeholder: '••••' },
                    ],
                  },
                ] as const).map(processor => {
                  const creds = settings.paymentCredentials?.[processor.id] as any;
                  const testMode = !!creds?.testMode;

                  // Common misconfigurations — surface them inline so the
                  // customer never sees a raw "Client Authentication failed"
                  // in production.
                  const fields = processor.fields as ReadonlyArray<{ key: string; label: string; secret: boolean; placeholder: string }>;
                  const secretFields = fields.filter(f => f.secret);
                  const publicFields = fields.filter(f => !f.secret);
                  const warnings: string[] = [];
                  for (const sf of secretFields) {
                    for (const pf of publicFields) {
                      const s = (creds?.[sf.key] || '').trim();
                      const p = (creds?.[pf.key] || '').trim();
                      if (s && p && s === p) {
                        warnings.push(`${sf.label} and ${pf.label} are identical — these should be two different values from the ${processor.label} dashboard.`);
                      }
                    }
                  }

                  return (
                    <div key={processor.id} className="mb-6 p-5 rounded-xl border border-[#1f2233] bg-[#0a0c13]/50">
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-white">{processor.label}</span>
                          <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-medium border ${
                            testMode
                              ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                              : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                          }`}>
                            {testMode ? 'Test Mode' : 'Live'}
                          </span>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 hover:text-white transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded bg-[#0a0c13] border-[#1f2233] text-emerald-500 focus:ring-emerald-500/20"
                            checked={testMode}
                            onChange={e => handleChange(`paymentCredentials.${processor.id}.testMode`, e.target.checked)}
                          />
                          <span>Test Mode (sandbox)</span>
                        </label>
                      </div>
                      {warnings.length > 0 && (
                        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-400">
                          {warnings.map((w, i) => (
                            <div key={i}>⚠ {w}</div>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {processor.fields.map(f => {
                          // Map { secret key → its "already configured" flag }
                          // so we can render a helpful placeholder and skip
                          // the "identical values" warning when the secret
                          // is just untouched.
                          const hasFlag = f.secret
                            ? (f.key === 'clientSecret' ? 'hasClientSecret'
                              : f.key === 'accessToken' ? 'hasAccessToken'
                              : f.key === 'privateKey' ? 'hasPrivateKey'
                              : null)
                            : null;
                          const alreadyConfigured = hasFlag ? !!creds?.[hasFlag] : false;
                          return (
                            <div key={f.key}>
                              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5 font-medium flex items-center gap-2">
                                <span>{f.label}</span>
                                {alreadyConfigured && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 normal-case tracking-normal">Configured</span>
                                )}
                              </label>
                              <input
                                type={f.secret ? 'password' : 'text'}
                                autoComplete="off"
                                spellCheck={false}
                                placeholder={alreadyConfigured ? '•••••• (leave blank to keep current)' : f.placeholder}
                                className="admin-input w-full bg-[#0a0c13] border-[#1f2233] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-lg text-white font-mono text-sm"
                                value={creds?.[f.key] || ''}
                                onChange={e => handleChange(`paymentCredentials.${processor.id}.${f.key}`, e.target.value)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Sticky Save bar — pinned to the viewport bottom, spans the full
          settings width. The content div above pads bottom-20 so it doesn't
          get hidden underneath. */}
      {showSaveBar && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 border-t border-[#1f2233] bg-[#0a0c13]/95 backdrop-blur-sm px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-end gap-3 z-30">
          <span className="text-xs text-slate-500 hidden sm:inline">Changes apply to the entire storefront.</span>
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="w-3.5 h-3.5" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="w-3.5 h-3.5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;
